import React, { useEffect, useMemo, useState } from "react";
import { Trophy, Certificate, ArrowRight, DownloadSimple, X, CheckCircle, XCircle } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { api, API } from "../lib/api";
import useSeo from "../lib/useSeo";
import { Link } from "react-router-dom";

const fmtNum = (n, d = 0) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtMoney = (n, sign = false) => {
  const v = Number(n || 0);
  const s = "$" + fmtNum(Math.abs(v), 0);
  return sign ? (v >= 0 ? "+" + s : "-" + s) : s;
};
const shortDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return ""; } };

// Rounded ring — pure SVG, no lib. Used in the modal's Win/Loss + R:R panels.
function Ring({ pct, size = 110, stroke = 12, color = "#10b981", label, children }) {
  const clamped = Math.max(0, Math.min(100, pct || 0));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (clamped / 100) * c;
  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(148,163,184,0.14)" strokeWidth={stroke} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={stroke} strokeLinecap="round"
          fill="none" strokeDasharray={`${dash} ${c - dash}`} style={{ transition: "stroke-dasharray 400ms ease" }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center font-display font-bold text-base sm:text-lg text-white">{label ?? children}</div>
    </div>
  );
}

// Sparkline equity curve — pure SVG from the closed_trades cumulative sum.
function EquityCurve({ trades, startBalance }) {
  const points = useMemo(() => {
    let bal = Number(startBalance || 0);
    const pts = [bal];
    (trades || []).forEach(t => { bal += Number(t.pnl || 0); pts.push(bal); });
    return pts;
  }, [trades, startBalance]);
  if (points.length < 2) return <div className="text-xs text-muted-foreground">Not enough trades to plot</div>;
  const min = Math.min(...points), max = Math.max(...points);
  const w = 300, h = 120, pad = 6;
  const scaleY = (v) => h - pad - ((v - min) / (max - min || 1)) * (h - 2 * pad);
  const scaleX = (i) => pad + (i / (points.length - 1)) * (w - 2 * pad);
  const d = points.map((p, i) => `${i === 0 ? "M" : "L"} ${scaleX(i).toFixed(1)} ${scaleY(p).toFixed(1)}`).join(" ");
  const positive = points[points.length - 1] >= points[0];
  const stroke = positive ? "#10b981" : "#ef4444";
  return <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-32"><path d={d} stroke={stroke} strokeWidth={2} fill="none" strokeLinecap="round" /></svg>;
}

function DetailModal({ token, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/prop-sim/shared/${token}`).then(({ data }) => setData(data)).catch(e => setErr(e?.response?.data?.detail || "Failed"));
  }, [token]);
  const closed = data?.closed_trades || [];
  const wins = closed.filter(t => Number(t.pnl) > 0);
  const losses = closed.filter(t => Number(t.pnl) < 0);
  const winRate = closed.length ? (wins.length / closed.length) * 100 : 0;
  const lossRate = 100 - winRate;
  const avgWin = wins.length ? wins.reduce((s, t) => s + Number(t.pnl), 0) / wins.length : 0;
  const avgLoss = losses.length ? Math.abs(losses.reduce((s, t) => s + Number(t.pnl), 0)) / losses.length : 0;
  const avgRR = avgLoss > 0 ? avgWin / avgLoss : 0;
  const profitFactor = avgLoss > 0 ? (wins.reduce((s, t) => s + Number(t.pnl), 0)) / (Math.abs(losses.reduce((s, t) => s + Number(t.pnl), 0)) || 1) : 0;
  const targetRR = 2.5;
  const rrPct = Math.min(100, (avgRR / targetRR) * 100);
  const isPass = data?.status === "passed";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="cert-modal">
      <div className="w-full max-w-4xl bg-card border border-border rounded-2xl shadow-2xl p-4 sm:p-8 my-4 sm:my-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <div className="font-display font-bold text-sm text-muted-foreground">{data?.challenge_label || "Certificate"}</div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded" data-testid="cert-modal-close"><X size={20} /></button>
        </div>
        {err && <div className="text-red-400 text-center py-10">{err}</div>}
        {!data && !err && <Skeleton className="h-96 rounded-xl" />}
        {data && (
          <>
            {/* Hero pass/fail */}
            <div className={`rounded-2xl border ${isPass ? "border-emerald-500/30 bg-emerald-500/5" : "border-red-500/30 bg-red-500/5"} p-5 sm:p-8 text-center mb-6 sm:mb-8`}>
              <div className={`mx-auto w-12 sm:w-14 h-12 sm:h-14 rounded-full flex items-center justify-center mb-3 sm:mb-4 ${isPass ? "bg-primary/20" : "bg-red-500/20"}`}>
                <Trophy size={24} weight="fill" className={isPass ? "text-primary" : "text-red-400"} />
              </div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Certificate of {isPass ? "Completion" : "Attempt"}</div>
              <h2 className="font-display font-black text-xl sm:text-3xl mb-2">Challenge {isPass ? "Passed" : "Failed"}</h2>
              <p className="text-muted-foreground text-xs sm:text-sm break-words">{data.challenge_label} · ${fmtNum(data.starting_balance, 0)} virtual · {data.trader_name} · {shortDate(data.created_at)}</p>
              <div className="text-[10px] sm:text-xs font-mono text-muted-foreground mt-3 break-all">Certificate ID {data.cert_id || token}</div>
              <a href={`${API}/prop-sim/shared/${token}/certificate.pdf`} target="_blank" rel="noreferrer" className="inline-block mt-4 sm:mt-5">
                <Button className="rounded-full bg-amber-500 hover:bg-amber-600 text-black text-sm h-10 px-5" data-testid="cert-download-pdf">
                  <DownloadSimple size={14} className="mr-2" /> Download certificate
                </Button>
              </a>
            </div>

            {/* Stats strip */}
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Trade analysis</div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3 mb-6">
              {[
                { label: "Total trades", val: fmtNum(closed.length, 0), tone: "" },
                { label: "Win rate", val: winRate.toFixed(0) + "%", tone: "text-emerald-400" },
                { label: "Avg R:R", val: avgRR.toFixed(1), tone: "" },
                { label: "Profit factor", val: profitFactor.toFixed(1), tone: "" },
                { label: "Max drawdown", val: (data.max_dd_pct || 0).toFixed(1) + "%", tone: "text-red-400" },
              ].map(s => (
                <div key={s.label} className="rounded-xl border border-border bg-white/[0.02] p-2.5 sm:p-3">
                  <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-muted-foreground">{s.label}</div>
                  <div className={`font-display font-black text-lg sm:text-xl mt-1 ${s.tone}`}>{s.val}</div>
                </div>
              ))}
            </div>

            {/* Three graphs */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-xl border border-border bg-white/[0.02] p-3 sm:p-4">
                <div className="text-sm font-semibold mb-3">Win / loss</div>
                <div className="flex items-center gap-4">
                  <Ring pct={winRate} size={96} stroke={11} color="#10b981" label={`${winRate.toFixed(0)}%`} />
                  <div className="text-xs space-y-1">
                    <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Win {winRate.toFixed(0)}%</div>
                    <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Loss {lossRate.toFixed(0)}%</div>
                  </div>
                </div>
              </div>
              <div className="rounded-xl border border-border bg-white/[0.02] p-3 sm:p-4">
                <div className="text-sm font-semibold mb-3">R:R vs {targetRR} target</div>
                <div className="flex items-center gap-4">
                  <Ring pct={rrPct} size={96} stroke={11} color="#f59e0b" label={`${rrPct.toFixed(0)}%`} />
                  <div className="text-xs space-y-1">
                    <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Avg {avgRR.toFixed(1)}:1</div>
                    <div className="text-muted-foreground">Target {targetRR}:1</div>
                  </div>
                </div>
              </div>
              <div className="rounded-xl border border-border bg-white/[0.02] p-3 sm:p-4">
                <div className="text-sm font-semibold mb-3">Equity curve</div>
                <EquityCurve trades={closed} startBalance={data.starting_balance} />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function Certificates() {
  useSeo({ title: "Certificates · Tradelog", canonical: "/certificates" });
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [openToken, setOpenToken] = useState(null);
  useEffect(() => {
    api.get("/prop-sim/certificates")
      .then(({ data }) => setRows(data?.certificates || []))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load"));
  }, []);

  return (
    <div className="p-6 sm:p-8 max-w-6xl mx-auto" data-testid="certificates-page">
      <h1 className="font-display font-black text-2xl sm:text-3xl">Certificates</h1>
      <p className="text-muted-foreground text-sm sm:text-base mt-1 mb-8">
        Every completed challenge attempt — pass or fail — with the full trade breakdown behind it.
      </p>

      {err && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-5 text-sm">{err}</div>
      )}
      {rows === null && !err && (
        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map(i => <Skeleton key={i} className="h-56 rounded-xl" />)}</div>
      )}
      {rows && rows.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
          <Trophy size={40} weight="duotone" className="mx-auto text-primary/70 mb-3" />
          <h3 className="font-display font-bold text-lg">No certificates yet</h3>
          <p className="text-muted-foreground text-sm mt-2 mb-5">Take a paper prop-firm challenge and every completed attempt shows up here.</p>
          <Link to="/prop-challenge"><Button className="rounded-full" data-testid="certs-take-challenge">Take a challenge <ArrowRight size={16} className="ml-1.5" /></Button></Link>
        </div>
      )}
      {rows && rows.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="certs-list">
          {rows.map(c => {
            const isPass = c.status === "passed";
            const netTone = Number(c.net_pnl || 0) >= 0 ? "text-emerald-400" : "text-red-400";
            return (
              <button key={c.token} onClick={() => setOpenToken(c.token)}
                className="text-left group rounded-2xl border border-border bg-card hover:border-primary/40 transition-colors overflow-hidden flex flex-col"
                data-testid={`cert-row-${c.token}`}>
                <div className={`h-1 w-full ${isPass ? "bg-emerald-500" : "bg-red-500"}`} />
                <div className="p-5 flex-1 flex flex-col gap-4">
                  <div>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${isPass ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
                      {isPass ? <CheckCircle size={12} weight="fill" /> : <XCircle size={12} weight="fill" />}
                      {c.status}
                    </span>
                    <div className="font-display font-bold text-lg mt-3 leading-snug">{c.firm_name} — {c.challenge_label?.split("—").slice(-1)[0]?.trim() || c.challenge_label}</div>
                    <div className="text-xs text-muted-foreground mt-1">${fmtNum(c.starting_balance, 0)} virtual · {c.trader_name}</div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 pt-2 border-t border-border">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Win rate</div>
                      <div className="font-display font-bold text-base mt-0.5">{Number(c.win_rate || 0).toFixed(0)}%</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Avg R:R</div>
                      <div className="font-display font-bold text-base mt-0.5">{Number(c.avg_rr || 0).toFixed(1)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Net P&L</div>
                      <div className={`font-display font-bold text-base mt-0.5 ${netTone}`}>{fmtMoney(c.net_pnl, true)}</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-border text-xs text-muted-foreground mt-auto">
                    <span>{shortDate(c.ended_at || c.started_at)}</span>
                    <span className="text-primary group-hover:translate-x-0.5 transition-transform">View certificate →</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
      {openToken && <DetailModal token={openToken} onClose={() => setOpenToken(null)} />}
    </div>
  );
}
